import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useEffect, useMemo, useState } from 'react';

import { useOrderState } from '../../../context';
import {
  JobDraftState,
  PublishInEnum,
  selectionOptions,
  Step,
  useJobDraftActions,
  useJobDraftState,
} from '../../context';
import BottomBar from '../BottomBar';
import Layout from '../Layout';

import SkillSelection from './SkillSelection';
import {
  createCustomShiftSupervisor,
  hasPendingCustomSupervisor,
} from './CreateContactForm';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import SingleColumnLayout from '@/components/SingleColumnLayout';
import Stack from '@/components/Stack';
import { GAEvent } from '@/constants/gaevents';
import Form from '@/form';
import useMediaQuery from '@/hooks/useMediaQuery';
import { Prettify } from '@/types';
import { JobTypeEnum, Scalars } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import { useOrderCreationTracking } from '../../useOrderCreationTracking';

export type FirstStepFormValues = Pick<
  JobDraftState,
  | 'skillCategory'
  | 'skill'
  | 'address'
  | 'addressInstructions'
  | 'contact'
  | 'contactInstructions'
  | 'shareContactNumber'
  | 'verification_enabled'
  | 'rateId'
>;

export type FirstStepFormFieldId = keyof FirstStepFormValues;
export type OnChangeFirstStepForm = (
  fieldContext: any,
  fieldId: Prettify<FirstStepFormFieldId>,
) => Scalars['Void'];

export enum FirstStepFieldIdEnum {
  SKILL_CATEGORY = 'skillCategory',
  SKILL = 'skill',
  ADDRESS = 'address',
  ADDRESS_INSTRUCTIONS = 'address_instructions',
}

type Props = {
  setStep: (step: Step) => Scalars['Void'];
  onClose: () => Scalars['Void'];
};

const SkillStep = ({ setStep, onClose }: Props) => {
  const {
    skillCategory: skillCategoryInContext,
    skill: skillInContext,
    address: addressInContext,
    addressInstructions: addressInstructionsInContext,
    contact: contactInContext,
    contactInstructions: contactInstructionsInContext,
    shareContactNumber: shareContactNumberInContext,
    verification_enabled: verificationEnabledInContext,
    schedules,
    rateId: rateIdInContext,
  } = useJobDraftState();
  const { updateSkill, updateSchedules, updatePublishing } =
    useJobDraftActions();
  const { orderType, billing } = useOrderState();

  const { logEvent } = useAnalytics();
  const { trackStepViewed, markStepCompleted } = useOrderCreationTracking();

  useEffect(() => {
    trackStepViewed('job_type');
  }, [trackStepViewed]);

  const startTime = new Date().getTime();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formValues, setFormValues] = useState<FirstStepFormValues>({
    skillCategory: skillCategoryInContext,
    skill: skillInContext,
    address: addressInContext,
    addressInstructions: addressInstructionsInContext,
    contact: contactInContext,
    contactInstructions: contactInstructionsInContext,
    shareContactNumber: shareContactNumberInContext,
    verification_enabled: verificationEnabledInContext,
    rateId: rateIdInContext,
  });

  const schedulesAlreadyCreated = schedules.length !== 0;
  const continueButtonIsDisabled = useMemo(() => {
    return (
      !formValues.skillCategory ||
      !formValues.skill ||
      !formValues.address ||
      !formValues.contact ||
      !formValues.rateId ||
      isSubmitting
    );
  }, [formValues, isSubmitting]);

  const handleSubmit = async ({
    skillCategory,
    skill,
    address,
    addressInstructions,
    contact,
    contactInstructions,
    shareContactNumber,
    verification_enabled,
  }: FirstStepFormValues): Promise<void> => {
    try {
      setIsSubmitting(true);

      let finalContact = contact;

      // Check if we need to create a custom shift supervisor
      if (hasPendingCustomSupervisor()) {
        try {
          const createdContact = await createCustomShiftSupervisor(
            billing?.customer.id!,
          );
          if (createdContact) {
            finalContact = createdContact;
          }
        } catch (error) {
          console.error('Failed to create custom shift supervisor:', error);
          // You might want to show an error message to the user here
          setIsSubmitting(false);
          return;
        }
      }

      if (
        schedulesAlreadyCreated &&
        (skillCategoryInContext?.id !== skillCategory?.id ||
          skillInContext?.id !== skill?.id)
      ) {
        // TODO: restart the  <TopBar />
        updateSchedules({ schedules: [] });
      }
      const _rateId =
        address?.rates && address?.rates.length > 0
          ? parseInt(address?.rates[0].id)
          : null;

      updateSkill({
        skillCategory: skillCategory!,
        skill: skill!,
        address: address!,
        addressInstructions: addressInstructions.trim(),
        contact: finalContact!,
        contactInstructions: contactInstructions.trim(),
        shareContactNumber: shareContactNumber,
        verification_enabled: verification_enabled,
        rateId: _rateId,
      });

      const isLTA = orderType === JobTypeEnum.LTA;
      logEvent(
        isLTA ? GAEvent.StepOneLta : GAEvent.StepOneGig,
        billing?.account?.id,
        {
          skill_ategory: skillCategory?.id,
          skill: skill?.id,
          address: address?.id,
          contact: finalContact?.id,
          time_spent: (new Date().getTime() - startTime) / 1000,
        },
      );

      if (isLTA) {
        updatePublishing({
          postSetting: selectionOptions.MY_SELECTIONS,
          publishIn: PublishInEnum.NEVER,
          publishJob: false,
        });
      }

      markStepCompleted('job_type');
      setStep(Step.SCHEDULE);
    } catch (error) {
      console.error('Error during form submission:', error);
      setIsSubmitting(false);
    }
  };

  const onChange: OnChangeFirstStepForm = (fieldContext, fieldId) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldId]: fieldContext.value,
    }));
    return { input: void 0, output: void 0 };
  };

  const phoneOnly = useMediaQuery('(max-width: 559px)');

  return (
    <Layout>
      <Form initialValues={formValues} onSubmit={handleSubmit}>
        <SingleColumnLayout
          noPadding={phoneOnly}
          size={phoneOnly ? 'full' : 'md'}
        >
          <Stack align="center" justify="center">
            {schedulesAlreadyCreated && (
              <Alert
                description="if you change the selected job category, skill or the address you will have to redo step 2"
                icon={faExclamationTriangle}
                status="warning"
                title="WARNING"
              />
            )}
          </Stack>
          <SkillSelection
            {...{ formValues, onChange, continueButtonIsDisabled }}
          />
          <BottomBar>
            <Button
              a11yLabel="Close job editor"
              appearance="outline"
              label="Cancel"
              type="button"
              onClick={onClose}
            />
            <Button
              a11yLabel="Submit form"
              disabled={continueButtonIsDisabled}
              id="continue-btn"
              isLoading={isSubmitting}
              label="Continue"
              type="submit"
            />
          </BottomBar>
        </SingleColumnLayout>
      </Form>
    </Layout>
  );
};

export default SkillStep;
